import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Logo from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { User, LogOut, Settings, LayoutDashboard, MessageSquare, Shield, Store, HelpCircle, CreditCard, Zap } from 'lucide-react';
import api from '../lib/api';

export const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (user) {
      loadCredits();
    }
  }, [user]);

  const loadCredits = async () => {
    try {
      const response = await api.get('/credits');
      setCredits(response.data);
    } catch (error) {
      console.error('Failed to load credits:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/questions', label: 'Sorular', icon: MessageSquare },
    { to: '/stores', label: 'Mağazalar', icon: Store },
    { to: '/ai-training', label: 'AI Eğitimi', icon: HelpCircle },
    { to: '/settings', label: 'Ayarlar', icon: Settings },
  ];

  if (isAdmin) {
    navLinks.push({ to: '/admin', label: 'Admin Panel', icon: Shield });
  }

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard">
              <Logo size="md" />
            </Link>
            
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`nav-${link.label.toLowerCase()}-link`}
                    className={
                      `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? 'bg-orange-50 text-[#FF6000]'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Credit Badge */}
            {credits && (
              <Link to="/credits">
                <Badge 
                  className={`flex items-center gap-1.5 px-3 py-1.5 cursor-pointer ${
                    credits.total_credits < 50 
                      ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200'
                  }`}
                >
                  <Zap className="h-3.5 w-3.5" />
                  <span className="font-semibold">{credits.total_credits}</span>
                  <span className="text-xs opacity-75">kredi</span>
                </Badge>
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2"
                  data-testid="navbar-user-menu"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6000] to-orange-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <span className="hidden sm:block font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="navbar-settings-item">
                  <Settings className="mr-2 h-4 w-4" />
                  Ayarlar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="navbar-logout-item">
                  <LogOut className="mr-2 h-4 w-4" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};
